import Link from "@/components/LocalizedLink";
import { ArrowRight, Check } from "lucide-react";
import Layout from "@/components/Layout";
import Section from "@/components/Section";
import HeroBackground from "@/components/HeroBackground";
import PrimaryCtaLink from "@/components/PrimaryCtaLink";
import Eyebrow from "@/components/Eyebrow";
import FAQAccordion from "@/components/FAQAccordion";
import IndustryCard from "@/components/IndustryCard";
import ProcessStep from "@/components/ProcessStep";
import ProofBar from "@/components/ProofBar";
import ServiceFlow from "@/components/ServiceFlow";
import SEO from "@/components/SEO";
import {
  SITE_URL,
  adjacentIndustryContexts,
  detailedIndustryGuides,
  getCtaLabel,
  implementationServices,
  localize,
  siteContent,
} from "@/content/site";
import { homeFaqs } from "@/content/home";
import { marketingRouteCopy } from "@/content/marketingRouteCopy";
import { proofTrustCopy, workEngagements } from "@/content/proofTrust";
import { useTranslation } from "@/i18n/useTranslation";
import { faqPageJsonLd, webPageJsonLd } from "@/seo/jsonLd";
import { usePageSeo } from "@/seo/usePageSeo";

const valueItems = [
  {
    code: "V/01",
    title: {
      en: "Reduce manual throughput bottlenecks",
      fr: "Réduire les goulots d’étranglement causés par le travail manuel",
    },
    description: {
      en: "Classify, route, draft, reconcile, and escalate work with review boundaries where judgment or customer impact matters.",
      fr: "Classer, acheminer, rédiger et rapprocher le travail, puis transmettre les exceptions, avec une validation humaine lorsque le jugement ou l’incidence sur la clientèle comptent.",
    },
    href: "/solutions/ai-agents-workflow-automation",
  },
  {
    code: "V/02",
    title: {
      en: "Turn messy documents into governed inputs",
      fr: "Transformer les documents complexes en entrées gouvernées",
    },
    description: {
      en: "Extract, validate, and review contracts, invoices, forms, support records, RFPs, and operational documents with traceable confidence and exception handling.",
      fr: "Extraire et valider les contrats, factures, formulaires, dossiers de soutien, appels d’offres et documents opérationnels, avec des seuils de confiance traçables et une gestion des exceptions.",
    },
    href: "/solutions/document-intelligence",
  },
  {
    code: "V/03",
    title: {
      en: "Improve recurring operating decisions",
      fr: "Améliorer les décisions opérationnelles récurrentes",
    },
    description: {
      en: "Forecast, score, prioritize, recommend, and route decisions where data exists but ownership, thresholds, and feedback still live in meetings.",
      fr: "Prévoir, évaluer, prioriser, recommander et acheminer des cas lorsque les données existent, mais que la responsabilité, les seuils et la rétroaction demeurent confinés aux réunions.",
    },
    href: "/solutions/predictive-models-decision-science",
  },
  {
    code: "V/04",
    title: {
      en: "Build the data layer AI can trust",
      fr: "Construire la couche de données dont le système a besoin",
    },
    description: {
      en: "Create source-of-truth pipelines, semantic definitions, access rules, quality checks, and activation layers before automation scales bad inputs.",
      fr: "Créer des chaînes de traitement de référence, des définitions sémantiques, des règles d’accès, des contrôles de qualité et des couches d’activation avant d’amplifier de mauvaises données.",
    },
    href: "/services/data-platforms",
  },
];

const processSteps = [
  {
    title: { en: "Choose the workflow", fr: "Choisir le processus" },
    description: {
      en: "Confirm the visible cost, owner, data reality, operating constraints, and whether AI is the right mechanism at all.",
      fr: "Confirmer le coût visible, la personne responsable, la réalité des données, les contraintes opérationnelles et la pertinence réelle de l’IA.",
    },
  },
  {
    title: { en: "Validate the operating case", fr: "Valider le bien-fondé opérationnel" },
    description: {
      en: "Baseline the current work, test representative cases, expose blockers, and define the smallest system worth funding and its acceptance criteria.",
      fr: "Établir la mesure de référence, tester des cas représentatifs, exposer les obstacles et définir le plus petit système qui mérite un investissement ainsi que ses critères d’acceptation.",
    },
  },
  {
    title: { en: "Deploy with control", fr: "Déployer avec contrôle" },
    description: {
      en: "Integrate the data, workflow logic, AI behaviour, human review, fallback paths, permissions, and measurement as one production system.",
      fr: "Intégrer les données, la logique du processus, le comportement de l’IA, la validation humaine, les solutions de repli, les autorisations et la mesure dans un seul système de production.",
    },
    marker: { en: "HUMAN /", fr: "HUMAIN /" },
  },
  {
    title: { en: "Operate and expand on evidence", fr: "Exploiter, puis élargir selon les résultats" },
    description: {
      en: "Monitor behaviour, exceptions, adoption, operating cost, and business impact. Expand authority or scope only when operating evidence supports it.",
      fr: "Surveiller le comportement, les exceptions, l’adoption, le coût d’exploitation et l’incidence opérationnelle. Élargir l’autorité ou le périmètre seulement lorsque les résultats observés le justifient.",
    },
  },
];

const consoleMetrics = [
  { code: "M/01", en: "Workflow candidate", fr: "Processus à évaluer" },
  { code: "M/02", en: "Data readiness", fr: "Préparation des données" },
  { code: "M/03", en: "Review boundary", fr: "Limite de validation" },
  { code: "M/04", en: "ROI baseline", fr: "Mesure de référence" },
];

const trustItems = [
  { en: "Evaluation harnesses", fr: "Bancs d’évaluation" },
  { en: "Prompt and workflow standards", fr: "Normes pour les instructions et les processus" },
  { en: "Human-in-the-loop controls", fr: "Contrôles de validation humaine" },
  { en: "Escalation logic", fr: "Règles de traitement des exceptions" },
  { en: "Observability and monitoring", fr: "Observabilité et surveillance" },
  { en: "Data quality checks", fr: "Contrôles de qualité des données" },
  { en: "Security, privacy, and governance", fr: "Sécurité, confidentialité et gouvernance" },
  { en: "ROI and adoption measurement", fr: "Mesure du rendement et de l’adoption" },
];

const Index = () => {
  const { locale } = useTranslation();
  const copy = marketingRouteCopy[locale].home;
  const primaryCtaLabel = getCtaLabel(siteContent.ctas.primary, locale);
  const productionSystemsCtaLabel = getCtaLabel(siteContent.ctas.productionSystems, locale);
  const secondaryCtaLabel = getCtaLabel(siteContent.ctas.secondary, locale);
  const proofCtaLabel = getCtaLabel(siteContent.ctas.proof, locale);
  const seo = usePageSeo("home");
  const services = implementationServices;
  const selectedEngagementId = "manual-triage-to-human-reviewed-ai" as const;
  const selectedEngagement = workEngagements[selectedEngagementId];
  const selectedEngagementTitle = proofTrustCopy.work.engagementTitles[selectedEngagementId];
  const faqItems = homeFaqs.map((item) => ({
    q: localize(item.q, locale),
    a: localize(item.a, locale),
  }));
  const heroSupportBullets = copy.heroSupportBullets;
  const heroMobileSubhead = localize(siteContent.brand.longPositioning, locale);
  const heroFlowNodes = copy.heroFlowNodes;

  const proofItems = [
    {
      code: "01",
      label: copy.proof.primaryFocus.label,
      value: copy.proof.primaryFocus.value,
    },
    {
      code: "02",
      label: copy.proof.deliveryLabel,
      value: localize(siteContent.proof.founderLedDelivery, locale),
    },
    {
      code: "03",
      label: copy.proof.evidence.label,
      value: copy.proof.evidence.value,
    },
    {
      code: "04",
      label: copy.proof.controls.label,
      value: copy.proof.controls.value,
    },
  ];

  const problemFailures = copy.problemFailures;
  const methodChecks = copy.methodChecks;
  const goodFit = copy.goodFit;
  const notFit = copy.notFit;

  const pageJsonLd = webPageJsonLd({
    url: SITE_URL,
    name: seo.title,
    description: seo.description,
  });
  const faqJsonLd = faqPageJsonLd(faqItems);

  return (
    <Layout>
      <SEO
        title={seo.title}
        description={seo.description}
        canonical={SITE_URL}
        keywords={seo.keywords}
        jsonLd={[pageJsonLd, faqJsonLd] as unknown as Record<string, unknown>[]}
      />

      <section className="homepage-hero page-hero-surface relative flex min-h-[92svh] items-start overflow-hidden grain md:min-h-[100svh] md:items-center lg:min-h-[92svh]">
        <HeroBackground quiet />
        <div className="absolute bottom-0 left-0 top-0 w-[2px] bg-primary/55" />
        <div className="container-wide relative z-10 w-full pb-12 pt-24 sm:pb-14 sm:pt-28 md:pb-20 md:pt-32 lg:pb-20 lg:pt-28">
          <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-[8fr_4fr] lg:gap-14">
            <div className="min-w-0 max-w-[52rem]">
              <div className="mb-5 opacity-0 animate-fade-up stagger-1 sm:mb-7">
                <Eyebrow
                  label={copy.hero.eyebrow}
                  code={copy.hero.code}
                  dark
                />
              </div>
              <div className="control-rule mb-6 w-20 opacity-0 animate-fade-up stagger-1 sm:mb-8 sm:w-24" />
              <h1 className="homepage-hero-title heading-hero mb-6 max-w-3xl text-[hsl(var(--sd-fg))] opacity-0 animate-fade-up stagger-2 sm:mb-8 lg:!text-[4.35rem] xl:!text-[4.6rem]">
                {localize(siteContent.brand.categoryStatement, locale)}
              </h1>
              <p className="mb-7 max-w-2xl text-[15.5px] leading-[1.66] text-content-secondary opacity-0 animate-fade-up stagger-3 sm:hidden">
                {heroMobileSubhead}
              </p>
              <p className="text-lead mb-7 hidden max-w-2xl text-content-secondary opacity-0 animate-fade-up stagger-3 sm:block">
                {localize(siteContent.brand.longPositioning, locale)}
              </p>
              <div className="flex flex-col gap-3 opacity-0 animate-fade-up stagger-4 sm:flex-row">
                <PrimaryCtaLink
                  className="btn-primary btn-hero group w-full justify-center sm:w-auto"
                  dataEventLocation="homepage_hero"
                  dataEventSurface="homepage"
                >
                  {primaryCtaLabel}
                  <ArrowRight className="h-3 w-3" />
                </PrimaryCtaLink>
                <PrimaryCtaLink
                  cta={siteContent.ctas.productionSystems}
                  className="btn-outline btn-hero group w-full justify-center sm:w-auto"
                  dataEventLocation="homepage_hero"
                  dataEventSurface="homepage"
                >
                  {productionSystemsCtaLabel}
                  <ArrowRight className="h-3 w-3" />
                </PrimaryCtaLink>
              </div>
              <ul className="mt-6 hidden max-w-2xl grid-cols-2 gap-x-6 gap-y-3 border-t border-[hsl(var(--sd-fg)/0.12)] pt-5 opacity-0 animate-fade-up stagger-5 md:grid">
                {heroSupportBullets.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-[13.5px] leading-[1.6] text-content-secondary">
                    <Check className="mt-1 h-3.5 w-3.5 shrink-0 text-primary" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative mx-auto hidden w-full max-w-md text-[hsl(var(--sd-fg))] opacity-0 animate-fade-up stagger-5 lg:block lg:max-w-none">
              <div className="command-shell-dark p-5 md:p-6">
                <div className="relative z-10">
                  <div className="flex items-center justify-between gap-4 border-b border-[hsl(var(--sd-fg)/0.09)] pb-4">
                    <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-primary">
                      {copy.hero.systemLabel}
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-content-on-dark">
                      {copy.hero.statusLabel}
                    </span>
                  </div>

                  <ServiceFlow nodes={[...heroFlowNodes]} stamp={copy.hero.flowStamp} />

                  <div className="mt-5 border-y border-[hsl(var(--sd-fg)/0.09)]">
                    {copy.hero.statusRows.map(([label, value]) => (
                      <div key={label} className="status-row first:border-t-0">
                        <span className="status-dot" aria-hidden="true" />
                        <span className="font-mono text-[9.5px] uppercase tracking-[0.16em] text-content-on-dark">
                          {label}
                        </span>
                        <span className="text-right text-[12.5px] leading-snug text-content-on-dark">
                          {value}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-5 grid grid-cols-2 gap-px border border-[hsl(var(--sd-fg)/0.09)] bg-[hsl(var(--sd-fg)/0.09)]">
                    {consoleMetrics.map((item) => (
                      <div key={item.code} className="bg-[hsl(var(--control-panel))] px-3 py-3">
                        <span className="block font-mono text-[9px] uppercase tracking-[0.14em] text-primary">
                          {item.code}
                        </span>
                        <span className="mt-1 block text-[12px] text-content-on-dark">
                          {localize(item, locale)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ProofBar items={proofItems} className="dark:hidden" />
      <ProofBar items={proofItems} dark className="hidden dark:block" />

      <Section>
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[0.72fr_1.28fr] lg:gap-20">
          <div className="min-w-0">
            <Eyebrow label={copy.problem.eyebrow} code="P / 01" className="mb-6" />
            <h2 className="heading-section max-w-2xl text-foreground">
              {copy.problem.headline}
            </h2>
          </div>
          <div className="min-w-0 border-t border-border/35 pt-8 lg:pt-10">
            <p className="max-w-2xl text-[16px] leading-[1.8] text-muted-foreground">
              {copy.problem.body}
            </p>
            <ul className="mt-8 divide-y divide-border/45 border-y border-border/45">
              {problemFailures.map((item) => (
                <li
                  key={item.code}
                  className="grid grid-cols-[3.25rem_1fr] items-start gap-x-4 gap-y-2 py-5 sm:grid-cols-[4.75rem_0.38fr_1fr] sm:gap-x-6 sm:gap-y-0"
                >
                  <span className="pt-0.5 font-mono text-[10px] uppercase tracking-[0.16em] text-primary">
                    P/{item.code}
                  </span>
                  <h3 className="text-[15px] font-semibold leading-snug text-foreground">{item.label}</h3>
                  <p className="col-start-2 text-[14px] leading-[1.72] text-muted-foreground sm:col-start-3 sm:max-w-xl">
                    {item.body}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      <Section fullBleed dark grain>
        <div className="container-wide section-padding relative z-10">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-[0.78fr_1.22fr] lg:gap-16 xl:gap-20">
            <div className="min-w-0">
              <Eyebrow label={copy.builds.eyebrow} code="B / 03" dark className="mb-6" />
              <h2 className="heading-section mb-6 max-w-xl text-[hsl(var(--sd-fg))]">
                {copy.builds.headline}
              </h2>
              <p className="max-w-lg text-[15px] leading-[1.8] text-content-on-dark">
                {copy.builds.body}
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row lg:flex-col xl:flex-row">
                <Link
                  to="/services/production-ai-systems"
                  className="btn-outline w-full justify-center sm:w-auto"
                  data-event="secondary_cta_click"
                  data-event-location="homepage_builds"
                  data-event-surface="builds_section"
                  data-event-target="/services/production-ai-systems"
                >
                  {copy.builds.exploreSystems}
                  <ArrowRight className="h-3 w-3" />
                </Link>
                <PrimaryCtaLink
                  cta={siteContent.ctas.proof}
                  className="btn-outline w-full justify-center sm:w-auto"
                  dataEventLocation="homepage_builds"
                  dataEventSurface="builds_section"
                >
                  {proofCtaLabel}
                  <ArrowRight className="h-3 w-3" />
                </PrimaryCtaLink>
              </div>
              <div className="mt-10 border-y border-[hsl(var(--sd-fg)/0.1)] py-5">
                <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-primary">
                  {copy.builds.buyerSignalLabel}
                </p>
                <p className="mt-3 max-w-md text-[14px] leading-[1.72] text-content-on-dark">
                  {copy.builds.buyerSignal}
                </p>
              </div>
              <div className="mt-8 border border-[hsl(var(--sd-fg)/0.12)] bg-[hsl(var(--sd-fg)/0.025)] p-5">
                <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-primary">
                  {copy.builds.selectedEngagementLabel}
                </p>
                <h3 className="mt-4 text-[17px] font-semibold leading-snug text-[hsl(var(--sd-fg)/0.9)]">
                  {localize(selectedEngagementTitle, locale)}
                </h3>
                <p className="mt-3 text-[13.5px] leading-[1.7] text-content-on-dark">
                  {localize(selectedEngagement.context, locale)}
                </p>
                <p className="mt-3 text-[13.5px] leading-[1.7] text-content-on-dark">
                  <span className="font-semibold text-[hsl(var(--sd-fg)/0.86)]">
                    {copy.builds.deliveredCapabilityPrefix}
                  </span>
                  {localize(selectedEngagement.deliveredCapability, locale)}
                </p>
                <p className="mt-3 text-[13.5px] leading-[1.7] text-content-on-dark">
                  <span className="font-semibold text-[hsl(var(--sd-fg)/0.82)]">
                    {copy.builds.measurementFramePrefix}
                  </span>
                  {selectedEngagement.measurementFrame.map((item) => localize(item, locale)).join(" · ")}
                </p>
                <p className="mt-3 text-[12px] leading-[1.65] text-content-on-dark">
                  {copy.builds.evidenceBoundary}
                </p>
                <div className="mt-5 flex flex-wrap gap-x-5 gap-y-3">
                  <Link
                    to={selectedEngagement.relatedService.href}
                    className="link-underline inline-flex text-[13px] text-content-on-dark"
                  >
                    {localize(selectedEngagement.relatedService.label, locale)}
                  </Link>
                  <Link to="/work" className="link-underline inline-flex text-[13px] text-content-on-dark">
                    {proofCtaLabel}
                  </Link>
                </div>
              </div>
            </div>

            <div className="min-w-0">
              <div className="system-card-dark p-5 md:p-6">
                <div className="flex flex-col gap-2 border-b border-[hsl(var(--sd-fg)/0.09)] pb-4 sm:flex-row sm:items-end sm:justify-between sm:pb-5">
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-primary">
                      {copy.builds.capabilityIndexLabel}
                    </p>
                    <h3 className="mt-2 text-[18px] font-semibold leading-snug text-[hsl(var(--sd-fg)/0.92)]">
                      {copy.builds.capabilityIndexHeadline}
                    </h3>
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-content-on-dark">
                    S / {String(services.length).padStart(2, "0")}
                  </span>
                </div>
                <div className="divide-y divide-[hsl(var(--sd-fg)/0.08)]">
                  {services.map((service, index) => (
                    <Link
                      key={service.slug}
                      to={service.href}
                      className="group grid gap-2 py-3.5 transition-colors duration-400 sm:grid-cols-[4.25rem_1fr_auto] sm:items-start sm:gap-3 sm:py-4"
                      data-event="service_page_cta_click"
                      data-event-surface="homepage_capability_index"
                      data-event-target={service.href}
                      data-event-service-slug={service.slug}
                    >
                      <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-primary">
                        S/{String(index + 1).padStart(2, "0")}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-[15px] font-semibold leading-snug text-[hsl(var(--sd-fg)/0.88)] transition-colors duration-400 group-hover:text-primary">
                          {localize(service.name, locale)}
                        </span>
                        <span className="mt-1.5 block max-w-2xl text-[13.5px] leading-[1.62] text-content-on-dark sm:mt-2 sm:leading-[1.68]">
                          {localize(service.description, locale)}
                        </span>
                      </span>
                      <ArrowRight className="mt-1 h-3.5 w-3.5 text-[hsl(var(--sd-fg)/0.42)] transition-all duration-400 group-hover:translate-x-1 group-hover:text-primary" />
                    </Link>
                  ))}
                </div>
              </div>

              <div className="mt-8">
                <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-primary">
                      {copy.builds.valuePatternsLabel}
                    </p>
                    <h3 className="mt-2 text-[18px] font-semibold leading-snug text-[hsl(var(--sd-fg)/0.92)]">
                      {copy.builds.valuePatternsHeadline}
                    </h3>
                  </div>
                  <p className="max-w-sm text-[13.5px] leading-[1.65] text-content-on-dark">
                    {copy.builds.valuePatternsBody}
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {valueItems.map((item) => (
                    <Link
                      key={item.code}
                      to={item.href}
                      className="system-card-dark group border-l-2 border-l-transparent p-4 transition-colors duration-400 hover:border-l-primary/60 hover:border-primary/35 sm:p-5"
                    >
                      <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-primary">{item.code}</span>
                      <h3 className="mt-4 text-[15px] font-semibold leading-snug text-[hsl(var(--sd-fg)/0.9)] transition-colors duration-400 group-hover:text-primary">
                        {localize(item.title, locale)}
                      </h3>
                      <p className="mt-2.5 text-[13.5px] leading-[1.65] text-content-on-dark sm:mt-3 sm:leading-[1.7]">
                        {localize(item.description, locale)}
                      </p>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section fullBleed animate className="surface-level-1">
        <div className="container-wide section-padding">
          <div className="relative overflow-hidden border border-primary/30 bg-background p-7 sm:p-10 lg:p-12">
            <div className="absolute bottom-0 left-0 top-0 w-[2px] bg-primary/70" aria-hidden="true" />
            <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-end lg:gap-16">
              <div>
                <Eyebrow
                  label={copy.review.eyebrow}
                  code="R / 01"
                  className="mb-6"
                />
                <h2 className="heading-section max-w-3xl text-foreground">
                  {copy.review.headline}
                </h2>
                <p className="mt-6 max-w-3xl text-[16px] leading-[1.8] text-muted-foreground">
                  {copy.review.body}
                </p>
                <dl className="mt-8 grid grid-cols-1 gap-px border border-border/45 bg-border/45 sm:grid-cols-3">
                  {copy.review.facts.map(({ label, value }) => (
                    <div key={label} className="bg-background p-5">
                      <dt className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-primary">{label}</dt>
                      <dd className="mt-3 text-[13.5px] font-semibold leading-snug text-foreground">{value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
              <div className="flex flex-col gap-3">
                <PrimaryCtaLink
                  cta={siteContent.ctas.verificationReview}
                  className="btn-primary group justify-center"
                  dataEventLocation="homepage_featured_review"
                  dataEventSurface="featured_review_section"
                >
                  {copy.review.applyLabel}
                  <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                </PrimaryCtaLink>
                <Link
                  to="/resources/sample-ai-verification-review"
                  className="btn-outline group justify-center"
                  data-event="secondary_cta_click"
                  data-event-location="homepage_featured_review"
                  data-event-surface="featured_review_section"
                  data-event-target="/resources/sample-ai-verification-review"
                >
                  {copy.review.sampleLabel}
                  <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section fullBleed animate>
        <div className="container-wide section-padding bg-surface-1">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-[0.74fr_1.26fr] lg:gap-16 xl:gap-20">
            <div className="min-w-0">
              <Eyebrow label={copy.operatingSystem.eyebrow} code="OS / 08" className="mb-6" />
              <h2 className="heading-section mb-6 max-w-xl text-foreground">
                {copy.operatingSystem.headline}
              </h2>
              <p className="max-w-lg text-[15px] leading-[1.8] text-muted-foreground">
                {copy.operatingSystem.body}
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row lg:flex-col xl:flex-row">
                <PrimaryCtaLink
                  className="btn-primary group w-full justify-center sm:w-auto"
                  dataEventLocation="homepage_midpage"
                  dataEventSurface="scope_to_production_section"
                >
                  {primaryCtaLabel}
                  <ArrowRight className="h-3 w-3" />
                </PrimaryCtaLink>
                <Link
                  to="/services"
                  className="btn-outline w-full justify-center sm:w-auto"
                  data-event="secondary_cta_click"
                  data-event-location="homepage_midpage"
                  data-event-surface="scope_to_production_section"
                  data-event-target="/services"
                >
                  {copy.operatingSystem.compareEngagements}
                  <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
              <div className="mt-10 border-t border-border/40 pt-6">
                <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-primary">
                  {copy.operatingSystem.gatesLabel}
                </p>
                <ul className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                  {methodChecks.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-[13.5px] leading-[1.6] text-muted-foreground">
                      <Check className="mt-1 h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="min-w-0">
              <div className="grid grid-cols-1 gap-7 lg:grid-cols-[0.72fr_1.28fr] lg:gap-10">
                <div className="system-card p-5 sm:p-6 md:p-7">
                  <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-primary">
                    {copy.operatingSystem.methodLabel}
                  </p>
                  <h3 className="mt-5 text-[18px] font-semibold leading-snug text-foreground">
                    {copy.operatingSystem.methodHeadline}
                  </h3>
                  <p className="mt-4 text-[14px] leading-[1.75] text-muted-foreground">
                    {copy.operatingSystem.methodBody}
                  </p>
                </div>
                <ul className="border-t border-border/35">
                  {processSteps.map((step, index) => (
                    <ProcessStep
                      key={localize(step.title, locale)}
                      code={`P/${String(index + 1).padStart(2, "0")}`}
                      title={localize(step.title, locale)}
                      description={localize(step.description, locale)}
                      marker={step.marker ? localize(step.marker, locale) : undefined}
                    />
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section fullBleed dark grain>
        <div className="container-wide section-padding relative z-10">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-[0.78fr_1.22fr] lg:gap-16 xl:gap-20">
            <div className="min-w-0">
              <Eyebrow label={copy.trust.eyebrow} code="T / 08" dark className="mb-6" />
              <h2 className="heading-section mb-6 max-w-xl text-[hsl(var(--sd-fg))]">
                {copy.trust.headline}
              </h2>
              <p className="max-w-lg text-[15px] leading-[1.8] text-content-on-dark">
                {copy.trust.body}
              </p>
              <PrimaryCtaLink
                cta={siteContent.ctas.secondary}
                className="btn-outline group mt-8 w-full justify-center sm:w-auto"
                dataEventLocation="homepage_trust"
                dataEventSurface="trust_section"
              >
                {secondaryCtaLabel}
                <ArrowRight className="h-3 w-3" />
              </PrimaryCtaLink>
            </div>

            <div className="min-w-0">
              <div className="grid grid-cols-2 gap-px border border-[hsl(var(--sd-fg)/0.1)] bg-[hsl(var(--sd-fg)/0.1)]">
                {trustItems.map((item, index) => (
                  <div key={localize(item, locale)} className="bg-[hsl(var(--control-panel))] p-4 sm:p-5 md:p-6">
                    <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-primary">
                      T/{String(index + 1).padStart(2, "0")}
                    </span>
                    <p className="mt-3 text-[13.5px] leading-[1.62] text-content-on-dark sm:mt-4 sm:text-[14px] sm:leading-[1.68]">
                      {localize(item, locale)}
                    </p>
                  </div>
                ))}
              </div>

              <div className="system-card-dark mt-8 p-6 md:p-7">
                <div className="grid grid-cols-1 gap-8 lg:grid-cols-[0.34fr_0.66fr]">
                  <div>
                    <Eyebrow label={copy.founder.eyebrow} code="F / 01" dark className="mb-6" />
                    <div className="control-rule mb-6 w-20" />
                    <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-content-on-dark">
                      Guillaume Belisle / Vert AI
                    </p>
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-[22px] font-semibold leading-tight text-[hsl(var(--sd-fg))] md:text-[26px]">
                      {copy.founder.headline}
                    </h3>
                    <p className="mt-5 max-w-2xl text-[15px] leading-[1.8] text-content-on-dark">
                      {copy.founder.body}
                    </p>
                    <div className="mt-7 flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-6">
                      <Link
                        to="/about"
                        className="link-underline inline-flex items-center gap-2 text-[13.5px] font-medium text-[hsl(var(--sd-fg)/0.86)] transition-colors duration-400 hover:text-primary"
                        data-event="secondary_cta_click"
                        data-event-location="homepage_founder"
                        data-event-surface="founder_section"
                        data-event-target="/about"
                      >
                        {copy.founder.aboutLink}
                        <ArrowRight className="h-3 w-3" />
                      </Link>
                      <Link
                        to="/insights"
                        className="link-underline inline-flex items-center gap-2 text-[13.5px] font-medium text-[hsl(var(--sd-fg)/0.86)] transition-colors duration-400 hover:text-primary"
                        data-event="insights_cta_click"
                        data-event-location="homepage_founder"
                        data-event-surface="founder_section"
                        data-event-target="/insights"
                      >
                        {copy.founder.insightsLink}
                        <ArrowRight className="h-3 w-3" />
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section fullBleed animate>
        <div className="container-wide section-padding bg-surface-1">
          <div className="mb-10 grid grid-cols-1 gap-8 lg:grid-cols-[0.8fr_1fr] lg:items-end lg:gap-16">
            <div className="min-w-0">
              <Eyebrow label={copy.industries.eyebrow} code="I / 05" className="mb-6" />
              <h2 className="heading-section mb-5 max-w-2xl text-foreground">
                {copy.industries.headline}
              </h2>
              <p className="max-w-2xl text-[15px] leading-[1.8] text-muted-foreground">
                {copy.industries.body}
              </p>
            </div>
            <div className="flex lg:justify-end">
              <Link
                to="/industries"
                className="btn-outline w-full justify-center sm:w-auto"
                data-event="secondary_cta_click"
                data-event-location="homepage_industries"
                data-event-surface="industries_section"
                data-event-target="/industries"
              >
                  {copy.industries.exploreLink}
                <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          </div>
          <section aria-labelledby="home-published-industry-contexts">
            <div className="mb-7 flex flex-col gap-3 border-t border-border/45 pt-7 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="label-sm mb-3">{copy.industries.publishedLabel}</p>
                <h3 id="home-published-industry-contexts" className="text-xl font-semibold leading-tight text-foreground sm:text-2xl">
                  {copy.industries.publishedTitle}
                </h3>
              </div>
              <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-content-secondary">
                03 / {copy.industries.publishedLabel}
              </span>
            </div>
            <ul className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
              {detailedIndustryGuides.map((industry, index) => (
                <li key={industry.slug}>
                  <IndustryCard
                    code={`I/${String(index + 1).padStart(2, "0")}`}
                    title={localize(industry.label, locale)}
                    description={localize(industry.description, locale)}
                    href={industry.href}
                    ctaLabel={localize(industry.ctaLabel, locale)}
                    typeLabel={copy.industries.publishedTypeLabel}
                    kind={industry.kind}
                    headingLevel={4}
                  />
                </li>
              ))}
            </ul>
          </section>

          <section className="mt-12 border-t border-border/45 pt-10" aria-labelledby="home-applicable-industry-contexts">
            <div className="mb-7 grid grid-cols-1 gap-5 lg:grid-cols-[0.8fr_1fr] lg:items-end lg:gap-16">
              <div>
                <p className="label-sm mb-3">{copy.industries.applicableLabel}</p>
                <h3 id="home-applicable-industry-contexts" className="text-xl font-semibold leading-tight text-foreground sm:text-2xl">
                  {copy.industries.applicableTitle}
                </h3>
              </div>
              <p className="max-w-2xl text-[14px] leading-[1.75] text-muted-foreground lg:justify-self-end">
                {copy.industries.applicableBody}
              </p>
            </div>
            <ul className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {adjacentIndustryContexts.map((industry, index) => (
                <li key={industry.slug}>
                  <IndustryCard
                    code={`A/${String(index + 1).padStart(2, "0")}`}
                    title={localize(industry.label, locale)}
                    description={localize(industry.description, locale)}
                    href={industry.href}
                    ctaLabel={localize(industry.ctaLabel, locale)}
                    typeLabel={copy.industries.applicableTypeLabel}
                    kind={industry.kind}
                    headingLevel={4}
                  />
                </li>
              ))}
            </ul>
          </section>
        </div>
      </Section>

      <Section narrow padding="sm">
        <Eyebrow label={copy.fit.eyebrow} code="FIT / 02" className="mb-6" />
        <h2 className="heading-section mb-5 max-w-2xl text-foreground">
          {copy.fit.headline}
        </h2>
        <p className="mb-10 max-w-2xl text-[15px] leading-[1.8] text-muted-foreground">
          {copy.fit.body}
        </p>
        <div className="grid grid-cols-1 gap-px bg-border/40 md:grid-cols-2">
          {[
            { label: copy.fit.goodFitLabel, items: goodFit, tone: "text-primary" },
            { label: copy.fit.notFitLabel, items: notFit, tone: "text-muted-foreground" },
          ].map((group) => (
            <div key={group.label} className="bg-background p-6 sm:p-8">
              <h3 className={`font-mono text-[10px] uppercase tracking-[0.16em] ${group.tone}`}>{group.label}</h3>
              <ul className="mt-5 space-y-4">
                {group.items.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-[14px] leading-[1.7] text-muted-foreground">
                    <Check className="mt-1 h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <PrimaryCtaLink cta={siteContent.ctas.proof} className="btn-outline mt-8 inline-flex">
          {proofCtaLabel}
          <ArrowRight className="h-3 w-3" aria-hidden="true" />
        </PrimaryCtaLink>
      </Section>

      <Section narrow padding="sm">
        <Eyebrow label="FAQ" code={`FAQ / ${String(faqItems.length).padStart(2, "0")}`} className="mb-6" />
        <h2 className="heading-section mb-8 text-foreground sm:mb-12">
          {copy.faqHeadline}
        </h2>
        <FAQAccordion items={faqItems} />
      </Section>

      <section className="relative overflow-hidden section-dark grain">
        <div className="shimmer-line" />
        <div className="container-wide section-padding relative z-10">
          <div className="control-panel mx-auto max-w-4xl p-7 sm:p-10 md:p-14">
            <Eyebrow label={copy.final.eyebrow} code="CTA / FINAL" dark className="mb-10" />
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.5fr_1fr] lg:items-end">
              <div className="min-w-0">
                <h2 className="heading-editorial mb-6 max-w-2xl text-[hsl(var(--sd-fg))]">
                  {copy.final.headline}
                </h2>
                <p className="max-w-xl text-[15px] leading-[1.8] text-content-on-dark">
                  {copy.final.body}
                </p>
              </div>
              <div className="flex flex-col gap-3 lg:items-end">
                <PrimaryCtaLink
                  className="btn-primary group w-full justify-center sm:w-auto"
                  dataEventLocation="homepage_final"
                  dataEventSurface="final_cta"
                >
                  {primaryCtaLabel}
                  <ArrowRight className="h-3 w-3" />
                </PrimaryCtaLink>
                <PrimaryCtaLink
                  cta={siteContent.ctas.secondary}
                  className="btn-outline group w-full justify-center sm:w-auto"
                  dataEventLocation="homepage_final"
                  dataEventSurface="final_cta"
                >
                  {secondaryCtaLabel}
                  <ArrowRight className="h-3 w-3" />
                </PrimaryCtaLink>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Index;
